import { Link, router } from "@inertiajs/react";
import { ArrowLeft } from "lucide-react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { FichaBody } from "@/components/ficha/FichaBody";
import { spaceMetrics } from "@/components/ficha/metrics";
import type { BookingSummary, PageProps } from "@/types";

interface Space {
  id: number;
  name: string;
  kind: string;
  color: string | null;
  active: boolean;
}

interface Props extends PageProps {
  space: Space;
  analytics: BookingSummary;
  can_edit?: boolean;
  can_delete?: boolean;
}

export default function SpaceShow({
  space,
  analytics,
  can_edit = false,
  can_delete = false,
  current_user,
  current_organization,
  current_business,
}: Props) {
  const orgSlug = current_organization?.slug || "";

  const handleDelete = () => {
    if (!confirm(`¿Eliminar el espacio "${space.name}"?`)) return;
    router.delete(`/${orgSlug}/spaces/${space.id}`);
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/spaces`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a espacios
          </Link>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">{space.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span>{space.kind === "equipment" ? "Equipo" : "Sala"}</span>
                {!space.active && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                    Inactivo
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {can_edit && (
                <Link href={`/${orgSlug}/spaces?edit=${space.id}`}>
                  <Button variant="outline">Editar</Button>
                </Link>
              )}
              {can_delete && (
                <Button variant="ghost" className="text-destructive" onClick={handleDelete}>
                  Eliminar
                </Button>
              )}
            </div>
          </div>
        </div>

        <FichaBody
          analytics={analytics}
          timezone={current_business?.timezone || "America/Mexico_City"}
          metrics={spaceMetrics(
            analytics,
            current_business?.timezone || "America/Mexico_City",
          )}
          hrefFor={({ period, pastPage }) => {
            const params = new URLSearchParams();
            params.set("period", period || analytics.period);
            if (pastPage && pastPage > 1) params.set("past_page", String(pastPage));
            return `/${orgSlug}/spaces/${space.id}?${params.toString()}`;
          }}
        />
      </div>
    </DashboardLayout>
  );
}
